/**
 * PUTs a file to a presigned storage URL (R2). Deliberately a bare `fetch`
 * with only a Content-Type header: the signature is in the URL, and adding
 * our Authorization header would make R2 reject the request.
 */
export async function putToSignedUrl(url: string, file: Blob): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
}

/** Width and height of an image file, or null when the browser can't decode it. */
export function imageSize(file: Blob): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    image.src = url;
  });
}

/** Image types the API accepts for logos and avatars, up to 2 MB. */
export const IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/svg+xml',
] as const;
export type ImageType = (typeof IMAGE_TYPES)[number];
const IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/**
 * What's wrong with a picked image, or null when it can be uploaded. `what`
 * names it in messages ("The logo must be…"); bitmaps must be at least
 * `minPx` on each side.
 */
export async function imageProblem(
  file: File,
  { what, minPx }: { what: string; minPx: number },
): Promise<string | null> {
  if (!(IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return 'Use a PNG, JPG, WebP or SVG image.';
  }
  if (file.size > IMAGE_MAX_BYTES) return `The ${what} must be 2 MB or smaller.`;
  if (file.type !== 'image/svg+xml') {
    const size = await imageSize(file);
    if (!size) return 'That image couldn’t be read. Try another file.';
    if (size.width < minPx || size.height < minPx) {
      return `Use an image at least ${minPx} px on each side.`;
    }
  }
  return null;
}

/**
 * `putToSignedUrl` with upload progress (0–100) and cancelling, for large
 * files. XHR, because fetch can't report upload progress. Aborting rejects
 * with an `AbortError`.
 */
export function putWithProgress(
  url: string,
  file: Blob,
  {
    onProgress,
    signal,
  }: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('content-type', file.type);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Upload failed (network)'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/** Photo types the API takes for a persona's reference look, up to 20 MB. */
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type PhotoType = (typeof PHOTO_TYPES)[number];
const PHOTO_MAX_BYTES = 20 * 1024 * 1024;

/** What's wrong with a picked photo, or null when it can be uploaded (at least `minPx` a side). */
export async function photoProblem(file: File, minPx = 512): Promise<string | null> {
  if (!(PHOTO_TYPES as readonly string[]).includes(file.type)) {
    return 'Use a JPG, PNG or WebP photo.';
  }
  if (file.size > PHOTO_MAX_BYTES) return 'The photo must be 20 MB or smaller.';
  const size = await imageSize(file);
  if (!size) return 'That photo couldn’t be read. Try another file.';
  if (size.width < minPx || size.height < minPx) {
    return `Use a photo at least ${minPx} px on each side.`;
  }
  return null;
}

/** `width` × `height` scaled down so neither side passes `maxPx` (never scaled up). */
export function fitWithin(
  width: number,
  height: number,
  maxPx: number,
): { width: number; height: number } {
  const scale = Math.min(1, maxPx / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

const COMPRESSIBLE = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * A photo made ready for upload: at most `maxPx` a side, re-encoded as JPEG
 * (the one image format every social platform takes), phone rotation baked
 * in and transparency flattened onto white. GIFs, SVGs and anything that
 * isn't a photo come back untouched, as does a JPEG the re-encode wouldn't
 * shrink. Best effort: a file the browser can't decode is returned as is.
 */
export async function compressPhoto(
  file: File,
  { maxPx = 2048, quality = 0.9 }: { maxPx?: number; quality?: number } = {},
): Promise<File> {
  if (!COMPRESSIBLE.has(file.type) || typeof createImageBitmap !== 'function')
    return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return file;
  }
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height, maxPx);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality),
    );
    if (!blob) return file;
    const resized = width !== bitmap.width || height !== bitmap.height;
    if (file.type === 'image/jpeg' && !resized && blob.size >= file.size) return file;
    const name = `${file.name.replace(/\.[^./]+$/, '') || 'photo'}.jpg`;
    return new File([blob], name, {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } finally {
    bitmap.close();
  }
}
